// The two files a tag run is taken away in. `pages/tagger.tsx` owns the clicks
// and the save; this module is the pure half — what goes in each file, and what
// the HTML one looks like.
//
// They answer different questions and are deliberately different FORMATS:
//
//   Download → an HTML REPORT. The reader is a person asking "what did this run
//   find?", usually someone who will not open a JSON file at all. It is one
//   self-contained page — no script, no fetch, no font or stylesheet from a
//   network — so it survives being mailed, dropped in a ticket or opened years
//   later off a disk, and it prints. It is a rendering of the run, not the run:
//   whatever is not on it is in the debug file.
//
//   Debug → EVERYTHING the console can see about the run, as one JSON object:
//   the run row, the tagger, every tag definition it could have asked (with the
//   subset it did ask marked), the per-answer counts, every finding, and every
//   captured input — the prompt, the transcript and the model's reply verbatim.
//   The tag definitions must stay: a finding names only a tag id, and its
//   definition otherwise lives only in the console.
import type { TagCount, TagDef, TagFinding, TagRunInput, TagRunRow, TaggerInfo } from "@bi/shared/types";

/** Everything both files are built from. Every field is what the page already
 *  holds or fetched for the click; nothing here fetches. */
export interface RunExport {
  run: TagRunRow;
  /** The tagger's own declaration. Absent only if the taggers list has not
   *  loaded, which the page's own gating makes unlikely rather than impossible. */
  tagger?: TaggerInfo | null;
  /** Every tag this tagger has, not only the ones this run asked — a reader
   *  comparing two runs needs to see that a tag EXISTED and was not asked. */
  tags: TagDef[];
  counts: TagCount[];
  findings: TagFinding[];
  /** Debug only. The page passes `[]` for the report. */
  inputs?: TagRunInput[];
  /** Injected so a test can pin it. */
  generatedAt?: string;
}

/** `<tagger>-run-<short id>`, the stem both files share. */
export function runFileStem(run: TagRunRow, tagger: string): string {
  return `${tagger}-run-${run.id.slice(0, 8)}`;
}

/** The full JSON bundle. Shape is documented by `_meta.about`, because the file
 *  outlives this console and lands in front of readers who never saw the page. */
export function runDebugBundle(x: RunExport): Record<string, unknown> {
  const asked = new Set(x.run.tagIds ?? []);
  const inputs = x.inputs ?? [];
  return {
    _meta: {
      about:
        "One tag run, complete: the run row, the tagger, every tag definition, the per-answer counts, every finding, and what each subject was sent and replied. Exported from the BI tagging console.",
      exportedAt: x.generatedAt ?? new Date().toISOString(),
      runId: x.run.id,
      tagger: x.run.tagger,
      counts: {
        tags: x.tags.length,
        tagsAsked: asked.size || x.tags.filter((t) => t.active).length,
        findings: x.findings.length,
        inputs: inputs.length,
      },
      // A reader of a finding-less file needs to know which of the two reasons
      // it is, and the file has to say so itself.
      inputsNote:
        inputs.length > 0
          ? null
          : "No captured inputs: the run predates the capture (migration 0192) or its rows passed the 14-day expiry. The findings outlive the words, deliberately.",
    },
    run: x.run,
    tagger: x.tagger ?? null,
    // `asked` is empty for a run that asked every active tag, which is how the
    // run row spells "all of them"; the flag then means active, not selected.
    tags: x.tags.map((t) => ({ ...t, askedByThisRun: asked.size === 0 ? t.active : asked.has(t.id) })),
    counts: x.counts,
    findings: x.findings,
    inputs,
  };
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** Every value on the page goes through this. A finding's evidence quote is
 *  CLIENT-AUTHORED text read out of a conversation, so the report is exactly
 *  the place an unescaped `<script>` would land in a colleague's browser. */
export function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ESCAPES[c] as string);
}

function num(n: number): string {
  return n.toLocaleString("en-US");
}

function pct(rate: number): string {
  return `${(rate * 100).toFixed(rate > 0 && rate < 0.01 ? 2 : 0)}%`;
}

function stamp(iso?: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 19).replace("T", " ") + " UTC";
}

/** Wall clock of the run, or "—" while it has no end. */
function duration(run: TagRunRow): string {
  if (!run.finishedAt) return "—";
  const ms = Date.parse(run.finishedAt) - Date.parse(run.createdAt);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return s % 60 === 0 ? `${m}m` : `${m}m ${s % 60}s`;
}

/** The scope as a sentence rather than a JSON blob: a report is read by people
 *  who want to know WHICH subjects, not which request body. */
export function scopeSummary(run: TagRunRow): string {
  const scope = (run.scope ?? {}) as Record<string, unknown>;
  const parts: string[] = [run.scopeKind];
  if (typeof scope.sinceDays === "number") parts.push(`last ${scope.sinceDays}d`);
  if (typeof scope.limit === "number") parts.push(`limit ${scope.limit}`);
  if (typeof scope.batchId === "string" && scope.batchId) parts.push(`corpus ${scope.batchId.slice(0, 8)}`);
  if (Array.isArray(scope.projectIds) && scope.projectIds.length)
    parts.push(`${scope.projectIds.length} explicit project(s)`);
  if (typeof scope.rerunOf === "string" && scope.rerunOf) parts.push(`rerun of ${scope.rerunOf.slice(0, 8)}`);
  const filters = scope.filters as Record<string, unknown> | undefined;
  if (filters) {
    const narrowed = Object.entries(filters).filter(
      ([, v]) => v !== null && v !== "any" && v !== false && v !== "",
    );
    if (narrowed.length) parts.push(narrowed.map(([k, v]) => `${k}=${String(v)}`).join(", "));
  }
  return parts.join(" · ");
}

/** Per-tag rollup for the report body. Built from `counts` when the server gave
 *  them and DERIVED from the findings when it did not, so the section is never
 *  the empty half of a half-loaded page. Tags the run asked but that fired for
 *  nobody are kept, with zero — "asked and never fired" is a result. */
export function tagRollup(x: RunExport): Array<{
  tagId: string;
  tagName: string;
  definition: string;
  answerType: string;
  fired: number;
  rate: number;
  answers: Array<{ answer: string; count: number; rate: number }>;
}> {
  const defs = new Map(x.tags.map((t) => [t.id, t]));
  const asked = new Set(x.run.tagIds ?? []);
  const byTag = new Map<string, { tagName: string; answers: Map<string, number>; fired: number }>();

  const touch = (tagId: string, tagName: string) => {
    let row = byTag.get(tagId);
    if (!row) {
      row = { tagName, answers: new Map(), fired: 0 };
      byTag.set(tagId, row);
    }
    if (!row.tagName) row.tagName = tagName;
    return row;
  };

  // Every tag the run could have asked gets a row first, so the zeroes exist.
  for (const t of x.tags) if (asked.size === 0 ? t.active : asked.has(t.id)) touch(t.id, t.name);

  if (x.counts.length > 0) {
    for (const c of x.counts) {
      const row = touch(c.tagId, c.tagName);
      row.fired += c.count;
      if (c.answer != null) row.answers.set(c.answer, (row.answers.get(c.answer) ?? 0) + c.count);
    }
  } else {
    for (const f of x.findings) {
      const row = touch(f.tagId, f.tagName);
      row.fired += 1;
      const answer = f.answer ?? "";
      if (answer) row.answers.set(answer, (row.answers.get(answer) ?? 0) + 1);
    }
  }

  const subjects = Math.max(x.run.nSubjects, 1);
  return [...byTag.entries()]
    .map(([tagId, row]) => {
      const def = defs.get(tagId);
      return {
        tagId,
        tagName: row.tagName || def?.name || tagId.slice(0, 8),
        definition: def?.definition ?? "",
        answerType: def?.answerType ?? "",
        fired: row.fired,
        rate: row.fired / subjects,
        answers: [...row.answers.entries()]
          .map(([answer, count]) => ({ answer, count, rate: count / Math.max(row.fired, 1) }))
          .sort((a, b) => b.count - a.count || a.answer.localeCompare(b.answer)),
      };
    })
    .sort((a, b) => b.fired - a.fired || a.tagName.localeCompare(b.tagName));
}
/** ONE subject's row of the matrix: what every tag said about it. */
export interface MatrixRow {
  subjectRef: string;
  subjectKind: string;
  label: string;
  nFindings: number;
  /** tagId → the findings that tag produced for this subject. Usually one; a
   *  multi-answer tag can produce several, and the cell says so rather than
   *  dropping the rest. */
  cells: Record<string, TagFinding[]>;
}

/** The findings, PIVOTED: one row per subject, one column per tag. Read across
 *  for "what did this run say about project X", down for "which subjects did
 *  this tag fire on".
 *
 *  Only subjects with at least one finding can be here: the run's subject list
 *  is not in the findings, and the report fetches nothing else. The panel says
 *  so, so a reader does not take the row count as the cover. */
export function subjectMatrix(x: RunExport): { columns: ReturnType<typeof tagRollup>; rows: MatrixRow[] } {
  const columns = tagRollup(x);
  const known = new Set(columns.map((c) => c.tagId));
  const bySubject = new Map<string, MatrixRow>();

  for (const f of x.findings) {
    let row = bySubject.get(f.subjectRef);
    if (!row) {
      row = {
        subjectRef: f.subjectRef,
        subjectKind: f.subjectKind,
        label: f.projectName || f.projectId || f.subjectRef,
        nFindings: 0,
        cells: {},
      };
      bySubject.set(f.subjectRef, row);
    }
    if (!row.label) row.label = f.projectName || f.projectId || f.subjectRef;
    row.nFindings += 1;
    (row.cells[f.tagId] ??= []).push(f);
    // A finding whose tag is not in the vocabulary the console loaded (an
    // archived tag, a tag from a draft schema) still gets a column: dropping it
    // would silently shrink the file to the tags we happen to recognize.
    if (!known.has(f.tagId)) {
      known.add(f.tagId);
      columns.push({
        tagId: f.tagId,
        tagName: f.tagName,
        definition: "",
        answerType: "",
        fired: 0,
        rate: 0,
        answers: [],
      });
    }
  }

  const rows = [...bySubject.values()].sort(
    (a, b) => b.nFindings - a.nFindings || a.label.localeCompare(b.label),
  );
  return { columns, rows };
}

/** The style block. Self-contained by rule: no font, no stylesheet, no script
 *  from a network, so the file renders identically offline and in an email
 *  client's sandbox. Dark is `prefers-color-scheme` only — a saved file has no
 *  toggle to remember a choice in.
 *
 *  The column filters are CSS, not script, and the per-tag rules are GENERATED
 *  below this block because they name each tag's own index. A browser without
 *  `:has()` drops those rules as invalid and renders every column, which is the
 *  right way round: the filters are a convenience and the data is the file. */
const REPORT_CSS = `
:root {
  color-scheme: light dark;
  --bg: #f6f7f9; --panel: #fff; --ink: #14161a; --muted: #5d6470; --line: #e3e6ea;
  --soft: #f0f2f5; --accent: #1c6fe0; --good: #14804a; --bad: #b42318; --bar: #cfe0f8;
  --shadow: 0 6px 24px rgba(16, 22, 32, .14);
}
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0f1216; --panel: #171b21; --ink: #e8eaed; --muted: #9aa3af; --line: #272d36;
    --soft: #1e242c; --accent: #6ba5f5; --good: #4ec27f; --bad: #f07167; --bar: #22364f;
    --shadow: 0 6px 24px rgba(0, 0, 0, .5);
  }
}
* { box-sizing: border-box; }
body { margin: 0; background: var(--bg); color: var(--ink);
  font: 14px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
.wrap { max-width: 1180px; margin: 0 auto; padding: 32px 20px 64px; }
header h1 { margin: 0 0 4px; font-size: 22px; letter-spacing: -0.01em; }
header .sub { color: var(--muted); font-size: 13px; }
.panel { background: var(--panel); border: 1px solid var(--line); border-radius: 12px; margin-top: 20px; }
.panel > h2 { margin: 0; padding: 12px 16px; font-size: 12px; text-transform: uppercase;
  letter-spacing: .07em; color: var(--muted); border-bottom: 1px solid var(--line);
  display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
.panel > h2 .note { text-transform: none; letter-spacing: 0; font-size: 12px; }
.panel .body { padding: 16px; }
.tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(154px, 1fr)); gap: 12px; margin-top: 20px; }
.tile { background: var(--panel); border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px; }
.tile .k { color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: .05em; }
.tile .v { font-size: 25px; font-weight: 600; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; }
.tile .n { color: var(--muted); font-size: 12px; margin-top: 2px; }
dl.meta { display: grid; grid-template-columns: max-content 1fr; gap: 6px 20px; margin: 0; }
dl.meta dt { color: var(--muted); }
dl.meta dd { margin: 0; overflow-wrap: anywhere; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th, td { text-align: left; padding: 8px 12px; border-top: 1px solid var(--line); vertical-align: top; }
thead th { color: var(--muted); font-weight: 600; font-size: 12px; border-top: 0;
  position: sticky; top: 0; z-index: 2; background: var(--panel);
  box-shadow: inset 0 -1px 0 var(--line); }
td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }

/* ---- the per-tag breakdown ---- */
.tag { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--line); }
.tag:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
.tag .name { font-weight: 600; }
.tag .def { color: var(--muted); margin: 2px 0 8px; }
.bars { display: grid; grid-template-columns: minmax(90px, max-content) 1fr max-content;
  gap: 5px 10px; align-items: center; }
.bars .lab { font-variant-numeric: tabular-nums; }
.bar { background: var(--bar); height: 10px; border-radius: 5px; min-width: 2px; }
.pill { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 12px;
  border: 1px solid var(--line); color: var(--muted); white-space: nowrap; }
.pill.done { color: var(--good); border-color: currentColor; }
.pill.error { color: var(--bad); border-color: currentColor; }
.pill.running, .pill.queued { color: var(--accent); border-color: currentColor; }
.empty { color: var(--muted); padding: 8px 0; }

/* ---- the filter controls ---- */
.controls { display: flex; flex-wrap: wrap; gap: 8px; padding: 12px 16px;
  border-bottom: 1px solid var(--line); align-items: center; }
.controls .lead { color: var(--muted); font-size: 12px; text-transform: uppercase;
  letter-spacing: .06em; margin-right: 2px; }
.chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px 3px 8px;
  border: 1px solid var(--line); border-radius: 999px; font-size: 13px; cursor: pointer;
  background: var(--panel); user-select: none; }
.chip:hover { border-color: var(--accent); }
.chip input { margin: 0; accent-color: var(--accent); cursor: pointer; }
.chip:has(input:checked) { border-color: var(--accent); color: var(--accent); }
.chip:has(input:focus-visible) { outline: 2px solid var(--accent); outline-offset: 2px; }
.chip .n { color: var(--muted); font-variant-numeric: tabular-nums; }
.chip:has(input:checked) .n { color: inherit; }
.controls-note { color: var(--muted); font-size: 12px; padding: 0 16px 12px; }
@supports selector(:has(*)) { .no-has { display: none; } }

/* ---- the matrix ---- */
.matrix-wrap { overflow-x: auto; border-radius: 0 0 12px 12px; }
/* Fixed layout, so one long free-text answer cannot decide every column's
   width — each tag gets the same share and truncates with an ellipsis, and the
   whole of it is in the hover card anyway. */
table.matrix { table-layout: fixed; }
table.matrix td, table.matrix th { border-right: 1px solid var(--line); }
table.matrix td:last-child, table.matrix th:last-child { border-right: 0; }
table.matrix tbody tr:hover { background: var(--soft); }
th.subject, td.subject { position: sticky; left: 0; z-index: 1; background: var(--panel);
  min-width: 220px; box-shadow: 1px 0 0 var(--line); }
thead th.subject { z-index: 3; }
tbody tr:hover td.subject { background: var(--soft); }
td.subject .ref { color: var(--muted); font-size: 12px; }
th.tag-col .tt { display: block; color: var(--ink); font-weight: 600; font-size: 13px;
  overflow: hidden; text-overflow: ellipsis; }
th.tag-col .ty { display: inline-block; margin-top: 3px; font-weight: 400; font-size: 11px; }
/* A cell is the hover target; the card is its child, drawn over everything. */
td.cell { position: relative; }
.val { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; border-bottom: 1px dotted var(--muted); cursor: help; }
.val.yes { color: var(--good); }
.val.no { color: var(--muted); }
.more { color: var(--muted); font-size: 12px; }
.none { color: var(--muted); }
.card { position: absolute; left: 0; top: calc(100% - 2px); z-index: 9; width: 320px;
  background: var(--panel); border: 1px solid var(--line); border-radius: 10px;
  box-shadow: var(--shadow); padding: 10px 12px; font-size: 12.5px; line-height: 1.45;
  visibility: hidden; opacity: 0; transition: opacity .08s ease; }
td.cell:hover .card, td.cell:focus-within .card { visibility: visible; opacity: 1; }
/* The wrap scrolls horizontally, which makes it clip vertically too, so a card
   hung below one of the last rows would be cut off. Those rows open upward. */
tbody tr:nth-last-child(-n+3) .card { top: auto; bottom: calc(100% - 2px); }
td.flip .card { left: auto; right: 0; }
.card .h { font-weight: 600; margin-bottom: 2px; }
.card .d { color: var(--muted); margin-bottom: 6px; }
.card .q { border-left: 2px solid var(--line); padding-left: 8px; margin: 6px 0;
  overflow-wrap: anywhere; }
.card .m { color: var(--muted); display: flex; flex-wrap: wrap; gap: 4px 10px; }
.card + .card { margin-top: 8px; }

footer { color: var(--muted); font-size: 12px; margin-top: 24px; }
@media print {
  body { background: #fff; }
  .panel, .tile { break-inside: avoid; border-color: #ccc; }
  .controls, .controls-note { display: none; }
  thead th { position: static; }
  th.subject, td.subject { position: static; }
  .card { display: none; }
  tbody tr:hover, tbody tr:hover td.subject { background: transparent; }
  .matrix-wrap { overflow: visible; }
}
`;

function tile(k: string, v: string, note = ""): string {
  return `<div class="tile"><div class="k">${escapeHtml(k)}</div><div class="v">${escapeHtml(v)}</div>${
    note ? `<div class="n">${escapeHtml(note)}</div>` : ""
  }</div>`;
}

/** The generated half of the stylesheet: two rules per tag column.
 *
 *  `col-<i>` hides a column the reader unticked; `only-<i>` (one radio group)
 *  hides every subject row that tag did not fire on. Both are written as
 *  `body:not(:has(...))` / `body:has(...)` so the DEFAULT — nothing ticked,
 *  nothing selected — is the whole table, and a browser that does not know
 *  `:has()` throws the rules away and shows the whole table too. */
function filterCss(n: number): string {
  const rules: string[] = [];
  for (let i = 0; i < n; i += 1) {
    rules.push(`body:not(:has(#col-${i}:checked)) .c-${i}{display:none}`);
    rules.push(`body:has(#only-${i}:checked) tr.row:not(.has-${i}){display:none}`);
  }
  rules.push(`body:has(#only-any:checked) tr.row:not(.any){display:none}`);
  return rules.join("\n");
}

/** One cell: the answer, plus a hover card carrying everything about the finding
 *  that does not fit in a column — the tag's definition, the evidence quote, the
 *  confidence and the latency. The card is CSS-only (`:hover`/`:focus-within`),
 *  and the cell is focusable so a keyboard reaches it. */
function matrixCell(findings: TagFinding[], col: { tagName: string; definition: string }): string {
  if (findings.length === 0) return `<span class="none">·</span>`;
  const first = findings[0] as TagFinding;
  const answer = first.answer ?? "fired";
  const tone = answer === "yes" ? " yes" : answer === "no" ? " no" : "";
  const cards = findings
    .map(
      (f) =>
        `<div class="card"><div class="h">${escapeHtml(col.tagName)} = ${escapeHtml(f.answer ?? "fired")}</div>` +
        (col.definition ? `<div class="d">${escapeHtml(col.definition)}</div>` : "") +
        (f.evidenceQuote ? `<div class="q">${escapeHtml(f.evidenceQuote)}</div>` : "") +
        `<div class="m"><span>confidence ${f.confidence.toFixed(2)}</span>` +
        (f.latencyMs ? `<span>${num(f.latencyMs)} ms</span>` : "") +
        (f.outcome && f.outcome !== "fired" ? `<span>${escapeHtml(f.outcome)}</span>` : "") +
        (f.createdAt ? `<span>${escapeHtml(stamp(f.createdAt))}</span>` : "") +
        `</div></div>`,
    )
    .join("");
  const extra = findings.length > 1 ? ` <span class="more">+${findings.length - 1}</span>` : "";
  return `<span class="val${tone}" tabindex="0">${escapeHtml(answer)}</span>${extra}${cards}`;
}

/** One self-contained HTML page: the run, its numbers, its per-tag breakdown and
 *  a subject × tag matrix of everything it found. */
export function runReportHtml(x: RunExport): string {
  const { run } = x;
  const { columns, rows } = subjectMatrix(x);
  const title = `${x.tagger?.label || run.tagger} · run ${run.id.slice(0, 8)}`;
  const latencies = x.findings.map((f) => f.latencyMs).filter((n) => n > 0);
  const avgLatency = latencies.length
    ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
    : 0;
  const perSubject = run.nSubjects > 0 ? run.nFindings / run.nSubjects : 0;
  const firedTags = columns.filter((c) => c.fired > 0).length;

  const tiles = [
    tile("Subjects", num(run.nSubjects), `${run.scopeKind} cover`),
    tile("Findings", num(run.nFindings), `${perSubject.toFixed(2)} per subject`),
    tile("Tags fired", `${firedTags} / ${columns.length}`, "asked by this run"),
    tile("Subjects tagged", num(rows.length), run.nSubjects ? `of ${num(run.nSubjects)} asked` : ""),
    tile(
      "Cost",
      `$${run.costUsd.toFixed(2)}`,
      run.nSubjects ? `$${(run.costUsd / run.nSubjects).toFixed(4)} per subject` : "",
    ),
    tile("Duration", duration(run), stamp(run.createdAt)),
    tile("Avg latency", avgLatency ? `${num(avgLatency)} ms` : "—", "per finding"),
  ].join("");

  const meta = [
    ["Run", run.id],
    ["Tagger", `${x.tagger?.label ?? ""} (${run.tagger})`.trim()],
    ["Subject", x.tagger?.subjectKind ?? "—"],
    ["Kind", run.runKind],
    ["Cover", scopeSummary(run)],
    ["Schema", `${run.schemaSource || "vendored"}${run.schemaSha ? ` · ${run.schemaSha.slice(0, 12)}` : ""}`],
    ["Started", stamp(run.createdAt)],
    ["Finished", stamp(run.finishedAt)],
    ["Requested by", run.requestedBy],
    ...(run.rerunOf ? [["Rerun of", run.rerunOf]] : []),
    ...(run.error ? [["Error", run.error]] : []),
  ]
    .map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`)
    .join("");

  const tagSections = columns.length
    ? columns
        .map((r) => {
          const max = Math.max(...r.answers.map((a) => a.count), r.fired, 1);
          const bars = r.answers.length
            ? `<div class="bars">${r.answers
                .map(
                  (a) =>
                    `<div class="lab">${escapeHtml(a.answer)}</div>` +
                    `<div class="bar" style="width:${Math.max((a.count / max) * 100, 1).toFixed(1)}%"></div>` +
                    `<div class="lab">${num(a.count)} · ${pct(a.rate)}</div>`,
                )
                .join("")}</div>`
            : r.fired > 0
              ? `<div class="empty">${num(r.fired)} finding(s); this tag stores no answer value.</div>`
              : `<div class="empty">Asked, never fired.</div>`;
          return (
            `<div class="tag"><div class="name">${escapeHtml(r.tagName)} ` +
            `<span class="pill">${escapeHtml(r.answerType || "—")}</span> ` +
            `<span class="pill">${num(r.fired)} of ${num(run.nSubjects)} subjects · ${pct(r.rate)}</span></div>` +
            (r.definition ? `<div class="def">${escapeHtml(r.definition)}</div>` : "") +
            bars
          );
        })
        .join("</div>") + "</div>"
    : `<div class="empty">No tags recorded for this run.</div>`;

  // The controls. Columns are checkboxes (all ticked); the row filter is ONE
  // radio group, because "show only the subjects this tag fired on" is a
  // question with one answer at a time — two at once would mean an intersection
  // nobody asked for and CSS would have to spell.
  const colChips = columns
    .map(
      (c, i) =>
        `<label class="chip"><input type="checkbox" id="col-${i}" checked>` +
        `<span>${escapeHtml(c.tagName)}</span><span class="n">${num(c.fired)}</span></label>`,
    )
    .join("");
  const rowChips = [
    `<label class="chip"><input type="radio" name="rowfilter" id="only-all" checked><span>All tagged subjects</span></label>`,
    `<label class="chip"><input type="radio" name="rowfilter" id="only-any"><span>Any finding</span></label>`,
    ...columns.map(
      (c, i) =>
        `<label class="chip"><input type="radio" name="rowfilter" id="only-${i}">` +
        `<span>${escapeHtml(c.tagName)} fired</span></label>`,
    ),
  ].join("");

  // `<colgroup>` rather than a width per cell: one declaration per column, and
  // the fixed layout above needs it to divide the table up.
  const cols =
    `<colgroup><col style="width:240px">` +
    columns.map((_, i) => `<col class="c-${i}" style="width:${Math.max(Math.round(760 / Math.max(columns.length, 1)), 120)}px">`).join("") +
    `</colgroup>`;

  const head =
    `<th class="subject">Subject</th>` +
    columns
      .map(
        (c, i) =>
          `<th class="tag-col c-${i}${i >= columns.length - 2 ? " flip" : ""}" title="${escapeHtml(
            c.definition ? `${c.tagName} — ${c.definition}` : c.tagName,
          )}">` +
          `<span class="tt">${escapeHtml(c.tagName)}</span>` +
          `<span class="ty pill">${escapeHtml(c.answerType || "—")}</span></th>`,
      )
      .join("");

  const body = rows.length
    ? rows
        .map((r) => {
          const marks = columns
            .map((c, i) => ((r.cells[c.tagId]?.length ?? 0) > 0 ? ` has-${i}` : ""))
            .join("");
          const cells = columns
            .map(
              (c, i) =>
                `<td class="cell c-${i}${i >= columns.length - 2 ? " flip" : ""}">` +
                `${matrixCell(r.cells[c.tagId] ?? [], c)}</td>`,
            )
            .join("");
          return (
            `<tr class="row${marks}${r.nFindings > 0 ? " any" : ""}">` +
            `<td class="subject"><div>${escapeHtml(r.label)}</div>` +
            `<div class="ref">${escapeHtml(r.subjectKind)} ${escapeHtml(r.subjectRef.slice(0, 14))}</div></td>` +
            cells +
            `</tr>`
          );
        })
        .join("")
    : `<tr><td class="subject">—</td><td class="cell" colspan="${Math.max(columns.length, 1)}">` +
      `No findings. Every subject was asked and nothing fired — the debug file has what each one was sent and replied.</td></tr>`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${REPORT_CSS}
${filterCss(columns.length)}</style></head>
<body><div class="wrap">
<header>
  <h1>${escapeHtml(x.tagger?.label || run.tagger)} <span class="pill ${escapeHtml(run.status)}">${escapeHtml(
    run.status,
  )}</span></h1>
  <div class="sub">Run ${escapeHtml(run.id.slice(0, 8))} · ${escapeHtml(stamp(run.createdAt))} · ${escapeHtml(
    run.requestedBy,
  )}</div>
  ${x.tagger?.description ? `<p class="sub">${escapeHtml(x.tagger.description)}</p>` : ""}
</header>

<div class="tiles">${tiles}</div>

<section class="panel">
  <h2>Findings <span class="note">${num(rows.length)} subject(s) × ${num(columns.length)} tag(s).${
    rows.length ? " Hover a value for the evidence." : ""
  }</span></h2>
  ${
    // A matrix with no rows has nothing to filter, and a control that changes
    // nothing reads as a broken one.
    rows.length
      ? `<div class="controls"><span class="lead">Columns</span>${colChips}</div>
  <div class="controls"><span class="lead">Rows</span>${rowChips}</div>`
      : ""
  }
  <p class="controls-note">
    Only subjects this run produced a finding for are listed${
      run.nSubjects > rows.length
        ? `; the other ${num(run.nSubjects - rows.length)} of ${num(run.nSubjects)} were asked and said nothing, and what they were sent is in the run's Debug JSON`
        : ""
    }.
    <span class="no-has">Column and row filters need a browser with <code>:has()</code>; this one shows every column instead.</span>
  </p>
  <div class="matrix-wrap"><table class="matrix">${cols}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>
</section>

<section class="panel"><h2>What was asked</h2><div class="body">${tagSections}</div></section>

<section class="panel"><h2>Run</h2><div class="body"><dl class="meta">${meta}</dl></div></section>

<footer>
  Exported ${escapeHtml(stamp(x.generatedAt ?? new Date().toISOString()))} from the BI tagging console.
  A finding is what the model concluded; the prompt it read and its reply verbatim are in the run's Debug JSON, not here.
</footer>
</div></body></html>`;
}
